import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal, Alert, Image, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/hooks/use-auth';
import { useProfile } from '@/hooks/useProfile';
import { useHousehold, useHouseholdMembers, useLeaveHousehold } from '@/hooks/useHousehold';
import { useGroceryLists } from '@/hooks/useGroceryLists';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Brand, Neutrals } from '@/constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

export default function Settings() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { settings, updateSettings, flashMessage, setFlashMessage } = useApp();
  const { profile } = useProfile();
  const { household, updateHouseholdName } = useHousehold();
  const { members: householdMembers } = useHouseholdMembers();
  const { leaveHousehold, loading: leaving } = useLeaveHousehold();
  const { lists: groceryLists, refetch: refetchGroceryLists } = useGroceryLists();
  const { user: authUser, signOut } = useAuth();
  const hasHousehold = !!profile?.household_id;

  // Re-sync grocery lists from cache when Settings gains focus so default list picker is up to date
  useFocusEffect(
    useCallback(() => {
      refetchGroceryLists();
    }, [refetchGroceryLists])
  );

  // Show success toast when returning from change-password (flash message set before navigate)
  const [showPasswordSuccessToast, setShowPasswordSuccessToast] = useState(false);
  useFocusEffect(
    useCallback(() => {
      if (flashMessage === 'passwordUpdated') {
        setFlashMessage(null);
        setShowPasswordSuccessToast(true);
        const t = setTimeout(() => setShowPasswordSuccessToast(false), 4000);
        return () => clearTimeout(t);
      }
    }, [flashMessage, setFlashMessage])
  );

  // When default list was deleted, clear or set to first available and notify user
  const [defaultListUpdateMessage, setDefaultListUpdateMessage] = useState<string | null>(null);
  const lastHandledDeletedDefaultId = useRef<string | null>(null);
  const previousListIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentListIds = new Set(groceryLists.map((list) => list.id));
    const previousListIds = previousListIdsRef.current;
    if (groceryLists.length === 0) {
      previousListIdsRef.current = currentListIds;
      return;
    }
    const defaultId = settings.defaultGroceryListId;
    if (!defaultId) {
      previousListIdsRef.current = currentListIds;
      return;
    }
    const exists = currentListIds.has(defaultId);
    if (exists) {
      lastHandledDeletedDefaultId.current = null;
      previousListIdsRef.current = currentListIds;
      return;
    }
    if (lastHandledDeletedDefaultId.current === defaultId) {
      previousListIdsRef.current = currentListIds;
      return;
    }
    const wasDeletedNow = previousListIds.has(defaultId);
    lastHandledDeletedDefaultId.current = defaultId;
    const firstId = groceryLists[0]?.id ?? null;
    updateSettings({ defaultGroceryListId: firstId });
    if (wasDeletedNow) {
      setDefaultListUpdateMessage(
        firstId
          ? "Your default list was deleted. We've selected your first list."
          : 'Your default list was deleted.'
      );
    }
    previousListIdsRef.current = currentListIds;
  }, [groceryLists, settings.defaultGroceryListId, updateSettings]);

  const [householdNameEdit, setHouseholdNameEdit] = useState('');
  const [householdNameSaving, setHouseholdNameSaving] = useState(false);
  const [showLeaveHouseholdModal, setShowLeaveHouseholdModal] = useState(false);

  useEffect(() => {
    if (household?.name != null) setHouseholdNameEdit(household.name);
  }, [household?.name]);

  const profileName =
    (authUser?.user_metadata?.full_name as string | undefined) ??
    (authUser?.user_metadata?.display_name as string | undefined) ??
    ((authUser?.email ? authUser.email.split('@')[0] : '') || '—');
  const profileEmail = authUser?.email ?? '—';
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const handleSignOut = () => {
    console.log('[Settings] Sign out button pressed');
    setShowSignOutModal(true);
  };

  const confirmSignOut = async () => {
    console.log('[Settings] User confirmed sign out');
    setShowSignOutModal(false);
    try {
      await signOut();
      console.log('[Settings] Sign out successful');
      // Navigate to sign-in explicitly; index.tsx redirect may not run when we're mounted in (tabs) (e.g. on iOS)
      router.replace('/(auth)/sign-in');
    } catch (error) {
      console.error('[Settings] Error signing out:', error);
      Alert.alert('Error', 'Failed to sign out. Please try again.');
    }
  };

  const handleSaveHouseholdName = async () => {
    const name = householdNameEdit.trim();
    if (!name || !household || name === household.name) return;
    setHouseholdNameSaving(true);
    try {
      await updateHouseholdName(name);
    } catch {
      Alert.alert('Error', 'Could not update household name.');
    } finally {
      setHouseholdNameSaving(false);
    }
  };

  const handleLeaveHouseholdPress = () => setShowLeaveHouseholdModal(true);

  const confirmLeaveHousehold = async () => {
    setShowLeaveHouseholdModal(false);
    try {
      const ok = await leaveHousehold();
      if (ok) router.replace('/(tabs)/settings');
    } catch (e) {
      Alert.alert('Error', e instanceof Error ? e.message : 'Failed to leave household.');
    }
  };

  return (
    <View style={styles.container} testID="settings-screen">
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        testID="settings-scroll"
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { paddingTop: 16 + insets.top }]}
      >
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <Ionicons name="settings-outline" size={24} color={Brand.primary} />
            <Text style={styles.headerTitle}>Settings</Text>
          </View>
          <Text style={styles.headerSubtitle}>Manage your account and preferences</Text>
        </View>

        {/* User Profile */}
        <Card style={styles.card}>
          <CardHeader style={styles.compactCardHeader}>
            <View style={styles.profileHeaderRow}>
              <View style={styles.cardTitleRow}>
                <Ionicons name="person" size={18} color={Brand.primary} />
                <CardTitle style={styles.compactCardTitle}>
                  <Text style={styles.cardTitleText}>Profile</Text>
                </CardTitle>
              </View>
              <Button
                variant="outline"
                size="sm"
                style={styles.profileEditButton}
                onPress={() => router.push('/change-email')}
              >
                Edit
              </Button>
            </View>
          </CardHeader>
          <CardContent style={styles.compactCardContent}>
            <View style={[styles.profileRow, styles.profileRowBorder]}>
              <Text style={styles.profileValue} numberOfLines={1}>{profileName}</Text>
            </View>
            <View style={styles.profileRow}>
              <Text style={styles.profileValue} numberOfLines={1}>{profileEmail}</Text>
            </View>
          </CardContent>
        </Card>

        {/* Household */}
        <Card style={styles.card} testID="household-section">
          <CardHeader style={styles.compactCardHeader}>
            <View style={styles.cardTitleRow}>
              <Ionicons name="people" size={18} color={Brand.primary} />
              <CardTitle style={styles.compactCardTitle}>
                <Text style={styles.cardTitleText}>Household</Text>
              </CardTitle>
            </View>
            <CardDescription>
              <Text style={styles.cardDescriptionText}>
                {hasHousehold
                  ? 'Share an invite link so others can join, or join another household with an invite link.'
                  : 'Create or join a household to share lists and more.'}
              </Text>
            </CardDescription>
          </CardHeader>
          <CardContent style={styles.compactCardContent}>
            {hasHousehold ? (
              <>
                <View style={styles.householdNameRow}>
                  <Text style={styles.profileLabel}>Household name</Text>
                  <View style={styles.householdNameInputRow}>
                    <Input
                      value={householdNameEdit}
                      onChangeText={setHouseholdNameEdit}
                      placeholder="e.g. Smith Family"
                      style={styles.householdNameInput}
                      containerStyle={styles.householdNameInputContainer}
                      editable={!householdNameSaving}
                    />
                    <Button
                      size="sm"
                      onPress={handleSaveHouseholdName}
                      disabled={householdNameSaving || (householdNameEdit.trim() === (household?.name ?? ''))}
                      style={styles.householdNameUpdateButton}
                    >
                      Update
                    </Button>
                  </View>
                </View>
                <View style={styles.membersSection} testID="household-member-list">
                  <Text style={styles.profileLabel}>Members ({householdMembers.length})</Text>
                  {householdMembers.map((member) => (
                    <View key={member.id} style={styles.memberRow}>
                      {member.avatar_url ? (
                        <Image source={{ uri: member.avatar_url }} style={styles.memberAvatar} />
                      ) : (
                        <View style={styles.memberAvatarPlaceholder}>
                          <Text style={styles.memberAvatarLetter}>
                            {(member.display_name ?? member.email ?? '?').charAt(0).toUpperCase()}
                          </Text>
                        </View>
                      )}
                      <View style={styles.memberInfo}>
                        <Text style={styles.memberName}>
                          {member.display_name || member.email || '—'}
                        </Text>
                        {member.display_name && (
                          <Text style={styles.memberEmail} numberOfLines={1}>
                            {member.email}
                          </Text>
                        )}
                      </View>
                      {member.id === authUser?.id && (
                        <Text style={styles.memberYouBadge}>You</Text>
                      )}
                    </View>
                  ))}
                </View>
                <View style={styles.householdActions}>
                  <Button
                    testID="invite-member-btn"
                    variant="outline"
                    onPress={() => router.push('/invite')}
                    style={styles.householdActionButton}
                  >
                    Invite member
                  </Button>
                  <Button
                    variant="outline"
                    onPress={() => router.push('/join-invite')}
                    style={styles.householdActionButton}
                  >
                    Join another household
                  </Button>
                  <Button
                    variant="outline"
                    onPress={handleLeaveHouseholdPress}
                    disabled={leaving}
                    style={[styles.householdActionButton, styles.leaveHouseholdButton]}
                  >
                    Leave household
                  </Button>
                </View>
              </>
            ) : (
              <View style={styles.householdActions}>
                <Button
                  onPress={() => router.push('/create-household')}
                  style={styles.householdActionButton}
                >
                  Create household
                </Button>
                <Button
                  variant="outline"
                  onPress={() => router.push('/join-invite')}
                  style={styles.householdActionButton}
                >
                  Join household
                </Button>
              </View>
            )}
          </CardContent>
        </Card>

        {/* App Preferences */}
        <Card style={styles.card}>
          <CardHeader style={styles.compactCardHeader}>
            <View style={styles.cardTitleRow}>
              <Ionicons name="settings-outline" size={18} color={Brand.primary} />
              <CardTitle style={styles.compactCardTitle}>
                <Text style={styles.cardTitleText}>App Preferences</Text>
              </CardTitle>
            </View>
          </CardHeader>
          <CardContent style={styles.compactCardContent}>
            <View style={styles.preferenceItem}>
              <Text style={styles.preferenceLabel}>Default Grocery List</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.listSelector}>
                {groceryLists.map(list => (
                  <TouchableOpacity
                    key={list.id}
                    onPress={() => updateSettings({ defaultGroceryListId: list.id })}
                    style={[
                      styles.listOption,
                      settings.defaultGroceryListId === list.id && styles.listOptionActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.listOptionText,
                        settings.defaultGroceryListId === list.id && styles.listOptionTextActive,
                      ]}
                    >
                      {list.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={styles.preferenceDescription}>
                This list will be selected by default when adding ingredients
              </Text>
            </View>

            <View style={styles.separator} />

            <View style={styles.preferenceItem}>
              <Text style={styles.preferenceLabel}>Checked Items Behavior</Text>
              <View style={styles.selectRow}>
                <TouchableOpacity
                  onPress={() => updateSettings({ checkedItemsBehavior: 'strikethrough' })}
                  style={[
                    styles.selectOption,
                    settings.checkedItemsBehavior === 'strikethrough' && styles.selectOptionActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.selectOptionText,
                      settings.checkedItemsBehavior === 'strikethrough' && styles.selectOptionTextActive,
                    ]}
                  >
                    Strike through
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => updateSettings({ checkedItemsBehavior: 'hide' })}
                  style={[
                    styles.selectOption,
                    settings.checkedItemsBehavior === 'hide' && styles.selectOptionActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.selectOptionText,
                      settings.checkedItemsBehavior === 'hide' && styles.selectOptionTextActive,
                    ]}
                  >
                    Hide
                  </Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.preferenceDescription}>
                How to display checked items in your grocery lists
              </Text>
            </View>
          </CardContent>
        </Card>

        {/* Account Management */}
        <Card style={styles.card}>
          <CardHeader style={styles.compactCardHeader}>
            <View style={styles.cardTitleRow}>
              <Ionicons name="lock-closed" size={18} color={Brand.primary} />
              <CardTitle style={styles.compactCardTitle}>
                <Text style={styles.cardTitleText}>Account Management</Text>
              </CardTitle>
            </View>
          </CardHeader>
          <CardContent style={styles.compactCardContent}>
            <Button
              variant="outline"
              style={styles.accountButton}
              onPress={() => router.push('/change-password')}
            >
              Change password
            </Button>
            <TouchableOpacity
              testID="logout-btn"
              style={[styles.accountButton, styles.signOutButton, styles.signOutButtonTouchable]}
              onPress={handleSignOut}
              activeOpacity={0.7}
            >
              <Ionicons name="log-out-outline" size={18} color={Neutrals.destructiveMuted} />
              <Text style={[styles.accountButtonText, styles.signOutButtonText]}>Sign Out</Text>
            </TouchableOpacity>
          </CardContent>
        </Card>

        <TouchableOpacity
          testID="delete-account-btn"
          style={styles.deleteAccountLink}
          onPress={() => router.push('/delete-account')}
          accessibilityRole="link"
          accessibilityLabel="Delete account"
          activeOpacity={0.7}
        >
          <Text style={styles.deleteAccountLinkText}>Delete account</Text>
        </TouchableOpacity>

        {/* Sign Out Confirmation Modal */}
        <Modal
          visible={showSignOutModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowSignOutModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Sign Out</Text>
              <Text style={styles.modalDescription}>
                Are you sure you want to sign out? You&apos;ll need to sign in again to access your account.
              </Text>
              <View style={styles.modalActions}>
                <Button variant="outline" onPress={() => setShowSignOutModal(false)} style={styles.modalButton}>
                  Cancel
                </Button>
                <Button testID="logout-confirm-btn" variant="destructive" onPress={confirmSignOut} style={styles.modalButton}>
                  Sign Out
                </Button>
              </View>
            </View>
          </View>
        </Modal>

        {/* Leave Household Confirmation Modal */}
        <Modal
          visible={showLeaveHouseholdModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowLeaveHouseholdModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Leave household</Text>
              <Text style={styles.modalDescription}>
                {householdMembers.length > 1
                  ? 'You will leave this household. You will no longer see its shared lists. Other members will keep the household.'
                  : 'You\'re the only member. If you leave, you\'ll no longer be in this household and will need to create or join one to use shared lists.'}
              </Text>
              <View style={styles.modalActions}>
                <Button variant="outline" onPress={() => setShowLeaveHouseholdModal(false)} style={styles.modalButton}>
                  Cancel
                </Button>
                <Button variant="destructive" onPress={confirmLeaveHousehold} style={styles.modalButton}>
                  Leave household
                </Button>
              </View>
            </View>
          </View>
        </Modal>

        {/* Default list updated notice */}
        <Modal
          visible={defaultListUpdateMessage !== null}
          transparent
          animationType="fade"
          onRequestClose={() => setDefaultListUpdateMessage(null)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Default list updated</Text>
              <Text style={styles.modalDescription}>{defaultListUpdateMessage}</Text>
              <View style={styles.modalActions}>
                <Button onPress={() => setDefaultListUpdateMessage(null)} style={styles.modalButton}>
                  OK
                </Button>
              </View>
            </View>
          </View>
        </Modal>

        {/* Password updated success toast */}
        <Modal
          visible={showPasswordSuccessToast}
          transparent
          animationType="fade"
          statusBarTranslucent
          onRequestClose={() => setShowPasswordSuccessToast(false)}
        >
          <View style={[styles.toastModalOverlay, { paddingBottom: insets.bottom + 80 }]}>
            <Pressable style={styles.toastDismissArea} onPress={() => setShowPasswordSuccessToast(false)} />
            <View style={styles.toastContainer} pointerEvents="box-none">
              <View style={styles.successToast}>
                <Ionicons name="checkmark-circle" size={22} color={Brand.primary} />
                <Text style={styles.successToastText}>Password updated successfully.</Text>
              </View>
            </View>
          </View>
        </Modal>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 24,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Brand.primary,
  },
  headerSubtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
  },
  card: {
    marginBottom: 12,
    borderRadius: 16,
  },
  compactCardHeader: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  compactCardContent: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 10,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  compactCardTitle: {
    marginBottom: 2,
  },
  cardTitleText: {
    fontSize: 17,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    lineHeight: 22,
  },
  cardDescriptionText: {
    fontSize: 13,
    color: Neutrals.textSecondary,
    marginTop: 2,
  },
  profileRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  profileRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Neutrals.borderLight,
  },
  profileHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  profileEditButton: {
    marginLeft: 8,
  },
  profileLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Neutrals.textSecondary,
    marginBottom: 4,
  },
  profileValue: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  householdActions: {
    gap: 8,
  },
  householdActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  householdNameRow: {
    marginBottom: 10,
  },
  householdNameInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  householdNameInputContainer: {
    flex: 1,
    marginBottom: 0,
  },
  householdNameInput: {
    flex: 1,
    marginTop: 2,
    marginBottom: 0,
    minHeight: 48,
  },
  householdNameUpdateButton: {
    height: 48,
    width: 80,
    marginBottom: 0,
  },
  membersSection: {
    marginBottom: 10,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: Neutrals.surface,
    borderRadius: 10,
    marginBottom: 6,
  },
  memberAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 10,
  },
  memberAvatarPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 10,
    backgroundColor: Brand.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  memberAvatarLetter: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  memberEmail: {
    fontSize: 11,
    color: Neutrals.textSecondary,
    marginTop: 1,
  },
  memberYouBadge: {
    fontSize: 12,
    fontWeight: '600',
    color: Brand.primary,
  },
  leaveHouseholdButton: {
    borderColor: Neutrals.destructiveMuted,
    marginTop: 4,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 8,
  },
  input: {
    flex: 1,
  },
  addButton: {
    alignSelf: 'flex-end',
  },
  badgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  badgeText: {
    fontSize: 12,
  },
  badgeRemove: {
    marginLeft: 4,
  },
  separator: {
    height: 1,
    backgroundColor: Neutrals.border,
    marginVertical: 10,
  },
  preferenceItem: {
    marginBottom: 10,
  },
  preferenceLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 6,
  },
  preferenceDescription: {
    fontSize: 12,
    color: Neutrals.textSecondary,
    marginTop: 4,
  },
  selectRow: {
    flexDirection: 'row',
    gap: 8,
  },
  selectOption: {
    flex: 1,
    padding: 10,
    borderRadius: 10,
    backgroundColor: Neutrals.surface,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectOptionActive: {
    backgroundColor: Brand.primary,
    borderColor: Brand.primary,
  },
  selectOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    textAlign: 'center',
  },
  selectOptionTextActive: {
    color: '#fff',
  },
  listSelector: {
    marginVertical: 4,
  },
  listOption: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: Neutrals.surface,
    marginRight: 6,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  listOptionActive: {
    backgroundColor: Brand.primary,
    borderColor: Brand.primary,
  },
  listOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  listOptionTextActive: {
    color: '#fff',
  },
  accountButton: {
    marginBottom: 8,
    width: '100%',
  },
  accountButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  signOutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  signOutButtonText: {
    color: Neutrals.destructiveMuted,
  },
  signOutButtonTouchable: {
    borderRadius: 10,
    borderWidth: 2,
    borderColor: Neutrals.destructiveMuted,
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginBottom: 8,
    width: '100%',
  },
  deleteAccountLink: {
    alignSelf: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 8,
    marginBottom: 24,
  },
  deleteAccountLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: Neutrals.textSecondary,
    textDecorationLine: 'underline',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
  toastModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
  },
  toastDismissArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  toastContainer: {
    alignItems: 'center',
    zIndex: 1,
  },
  successToast: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 6,
    maxWidth: '100%',
  },
  successToastText: {
    flex: 1,
    fontSize: 14,
    color: Neutrals.textPrimary,
  },
});
